import React, { useState } from "react";
import {
	Box,
	Button,
	FormHelperText,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Checkbox,
	FormControlLabel,
	CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import customHttp from "../../services/customHTTP";
import PageTitle from "../../components/PageTitle/PageTitle";
import { isValidEmail, getEmailErrorMessage } from "../../utilities/emailValidator";
import "./Register.css";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import AuthedUser from "../AuthedUser/AuthedUser";
import { useAuth } from "../../context/authContext";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ASSETS_BASE_URL } from "../../config";

const roleOptions = [
	{ key: "viewer", value: "Viewer", text: "Viewer" },
	{ key: "purchaser", value: "Purchaser", text: "Purchaser" },
];

const Register = () => {
	const [formData, setFormData] = useState({
		user_email: "",
		user_password: "",
		confirm_password: "",
		user_first_name: "",
		user_last_name: "",
		user_phone: "",
		user_display_name: "",
		user_role: "Viewer",
		user_status: 0,
		company_name: "",
		street_address: "",
		terms_agreed: false, // For storing checkbox state
		purchaser_authority: false, // For storing second checkbox state
	});
	const [error, setError] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const [emailError, setEmailError] = useState("");
	const [termsError, setTermsError] = useState(""); // For terms validation
	const [loading, setLoading] = useState(false);
	const [validation, setValidation] = useState({
		minLength: false,
		uppercase: false,
		specialChar: false,
		number: false,
		confirmPass: false,
	});
	const navigate = useNavigate();
	const { isAuthenticated } = useAuth();

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		if (type === "checkbox") {
			setFormData({ ...formData, [name]: checked });
		} else {
			setFormData({ ...formData, [name]: value });
		}

		if (name === "user_email") {
			setEmailError("");
		}
		if (name === "user_password" || name === "confirm_password") {
			setPasswordError("");
		}
	};

	const validatePassword = (password) => {
		const minLength = password.length >= 8;
		const uppercase = /[A-Z]/.test(password);
		const specialChar = /[!@#$%^&*]/.test(password);
		const number = /[0-9]/.test(password);

		setValidation({
			minLength,
			uppercase,
			specialChar,
			number,
		});
	};

	const validateConfirmPassword = (confirmPasswd) => {
		const confirmpass = formData.user_password === confirmPasswd;
		setValidation({
			...validation,
			confirmPass: confirmpass,
		});
	};

	const handlePasswordChange = (e) => {
		const newPassword = e.target.value;
		setFormData({
			...formData,
			user_password: newPassword,
		});
		validatePassword(newPassword);
	};

	const handleConfirmPasswordChange = (e) => {
		setFormData({
			...formData,
			confirm_password: e.target.value,
		});
		validateConfirmPassword(e.target.value);
	};

	const isFormValid = () => {
		return (
			validation.minLength &&
			validation.uppercase &&
			validation.specialChar &&
			validation.number &&
			formData.user_password === formData.confirm_password
		);
	};

	// Email validation is now imported from utilities

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		setTermsError("");
		setLoading(true);

		const req_body = {
			...formData,
			user_display_name: `${formData.user_first_name} ${formData.user_last_name}`,
		};

		// Password and email validation
		if (formData.user_password !== formData.confirm_password) {
			setPasswordError("Passwords do not match");
			setLoading(false);
			return;
		}

		if (!isValidEmail(formData.user_email)) {
			setEmailError(getEmailErrorMessage(formData.user_email));
			setLoading(false);
			return;
		}

		// Terms and conditions validation for "Purchaser" role
		if (formData.user_role === "Purchaser" && (!formData.terms_agreed || !formData.purchaser_authority)) {
			setTermsError("You must agree to both terms to proceed.");
			setLoading(false);
			return;
		}

		try {
			const response = await customHttp.post("/auth/register", req_body);
			if (response.status === 201) {
				const flowToken = response.data?.flow_token || null;
				if (!flowToken) {
					throw new Error("Registration session token was not returned.");
				}
				sessionStorage.setItem("registration_flow_token", flowToken);
				await Swal.fire({
					title: "OTP Sent",
					text: "Check your email for the verification code.",
					icon: "success",
					showCancelButton: false,
				});

				navigate("/verify-email", {
					state: {
						flowToken,
						userEmail: formData.user_email,
					},
				});
			}
		} catch (error) {
			console.error("Registration error:", error);
			setError(error.response?.data?.message || error.message || "An error occurred. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const AuthPageStyles = {
		backgroundImage: `url('${ASSETS_BASE_URL}/home_bg2.jpg')`,
		backgroundSize: "cover",
		backgroundPosition: "center",
	};

	if (isAuthenticated) {
		return <AuthedUser />;
	}

	return (
		<Box className="register-container" sx={AuthPageStyles}>
			<Box className="register-form-container">
				<img src="/logo.png" alt="Logo" className="register-logo" />
				<div className="form-container">
					<form className="register-form" onSubmit={handleSubmit}>
						<PageTitle title="Request for New Account" style />
						<Box className="user-details-group">
							<h3>User Details</h3>
							<Grid container spacing={2}>
								<Grid size={{ xs: 12, md: 4 }}>
									<TextField
										label="First Name"
										variant="outlined"
										fullWidth
										required
										name="user_first_name"
										value={formData.user_first_name}
										onChange={handleChange}
										size="small"
									/>
								</Grid>
								<Grid size={{ xs: 12, md: 4 }}>
									<TextField
										label="Last Name"
										variant="outlined"
										fullWidth
										required
										name="user_last_name"
										value={formData.user_last_name}
										onChange={handleChange}
										size="small"
									/>
								</Grid>

								<Grid size={{ xs: 12, md: 4 }}>
									<TextField
										label="Phone Number"
										variant="outlined"
										fullWidth
										required
										name="user_phone"
										value={formData.user_phone}
										onChange={handleChange}
										size="small"
									/>
								</Grid>
								<Grid size={{ xs: 12, md: 4 }}>
									<TextField
										label="Email"
										variant="outlined"
										fullWidth
										required
										name="user_email"
										type="email"
										value={formData.user_email}
										onChange={handleChange}
										size="small"
									/>
									{emailError && <FormHelperText error>{emailError}</FormHelperText>}
								</Grid>
								<Grid size={{ xs: 12, md: 4 }}>
									<TextField
										label="Password"
										variant="outlined"
										fullWidth
										required
										name="user_password"
										type="password"
										value={formData.user_password}
										onChange={handlePasswordChange}
										size="small"
									/>
								</Grid>
								<Grid size={{ xs: 12, md: 4 }}>
									<TextField
										label="Confirm Password"
										variant="outlined"
										fullWidth
										required
										name="confirm_password"
										type="password"
										value={formData.confirm_password}
										onChange={handleConfirmPasswordChange}
										size="small"
									/>
								</Grid>

								{passwordError && <FormHelperText error>{passwordError}</FormHelperText>}

								<div className="password-validation">
									<p
										style={{
											fontSize: "11px",
											color: validation.minLength ? "green" : "#aaa",
											marginBottom: 0,
										}}>
										{validation.minLength ? (
											<CheckCircleIcon fontSize="11px" />
										) : (
											<ErrorOutlineIcon fontSize="11px" />
										)}{" "}
										Minimum 8 characters
									</p>
									<p
										style={{
											fontSize: "11px",
											color: validation.uppercase ? "green" : "#aaa",
											marginBottom: 0,
										}}>
										{validation.uppercase ? (
											<CheckCircleIcon fontSize="11px" />
										) : (
											<ErrorOutlineIcon fontSize="11px" />
										)}{" "}
										At least one uppercase letter
									</p>
									<p
										style={{
											fontSize: "11px",
											color: validation.specialChar ? "green" : "#aaa",
											marginBottom: 0,
										}}>
										{validation.specialChar ? (
											<CheckCircleIcon fontSize="11px" />
										) : (
											<ErrorOutlineIcon fontSize="11px" />
										)}{" "}
										At least one special character
									</p>
									<p
										style={{
											fontSize: "11px",
											color: validation.number ? "green" : "#aaa",
											marginBottom: 0,
										}}>
										{validation.number ? (
											<CheckCircleIcon fontSize="11px" />
										) : (
											<ErrorOutlineIcon fontSize="11px" />
										)}{" "}
										At least one number
									</p>
									<p
										style={{
											fontSize: "11px",
											color: validation.confirmPass ? "green" : "#aaa",
											marginBottom: 0,
										}}>
										{validation.confirmPass ? (
											<CheckCircleIcon fontSize="11px" />
										) : (
											<ErrorOutlineIcon fontSize="11px" />
										)}{" "}
										Confirm Password Matches
									</p>
								</div>
							</Grid>
						</Box>
						<Box
							display="flex"
							flexDirection="row"
							justifyContent="flex-start"
							alignItems="flex-start"
							columnGap={2}
							width="100%">
							<Box className="company-details-group" mt={2} sx={{ flex: 1 }}>
								<h3>Company Details</h3>
								<Grid container spacing={2}>
									<Grid size={{ xs: 12, md: 12 }}>
										<TextField
											label="Company Name"
											variant="outlined"
											fullWidth
											required
											name="company_name"
											value={formData.company_name}
											onChange={handleChange}
											size="small"
										/>
									</Grid>
									<Grid size={{ xs: 12, md: 12 }}>
										<TextField
											label="Street Address"
											variant="outlined"
											fullWidth
											required
											name="street_address"
											value={formData.street_address}
											onChange={handleChange}
											size="small"
										/>
									</Grid>
								</Grid>
							</Box>

							<Box className="role-selection" mt={2} sx={{ flex: 1 }}>
								<h3>User Role</h3>
								<FormControl fullWidth size="small" sx={{ width: "100%" }}>
									<InputLabel>Select Role</InputLabel>
									<Select
										label="Select Role"
										name="user_role"
										value={formData.user_role}
										onChange={handleChange}
										sx={{ width: "100%" }}>
										{roleOptions.map((role) => (
											<MenuItem key={role.key} value={role.value}>
												{role.text}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Box>
						</Box>

						{/* Show terms and conditions checkboxes for Purchaser role */}
						{formData.user_role === "Purchaser" && (
							<Box className="terms-conditions" mt={4}>
								<h3>Terms and Conditions</h3>
								<FormControlLabel
									control={
										<Checkbox
											checked={formData.terms_agreed}
											onChange={handleChange}
											name="terms_agreed"
										/>
									}
									label="I confirm that the role I have selected is reflective of the rights and authorization I hold within my company"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={formData.purchaser_authority}
											onChange={handleChange}
											name="purchaser_authority"
										/>
									}
									label="I confirm that as a purchaser, I have the authority to place purchase orders on behalf of my company which creates binding contractual agreements"
								/>
								{termsError && <FormHelperText error>{termsError}</FormHelperText>}
							</Box>
						)}

						<p style={{ fontSize: "11px", color: "#aaa", marginTop: "10px" }}>
							Note: This request will be reviewed by our support team and you will be notified via email
							once your account is approved.
						</p>

						{error && <p className="error-text">{error}</p>}

						<div className="register-button">
							<Button
								disabled={!isFormValid() || loading}
								variant="contained"
								color="primary"
								type="submit"
								sx={{ minWidth: "120px" }}>
								{loading ? (
									<>
										<CircularProgress size={18} sx={{ color: "white", mr: 1 }} />
										Submitting...
									</>
								) : (
									"Request for Account"
								)}
							</Button>
						</div>
						<div className="login-link-container">
							<Link to="/login">Already a user? Login</Link>
						</div>
					</form>
				</div>
			</Box>
		</Box>
	);
};

export default Register;
